<script setup lang="ts">
import type { DateRangePickerFieldValue } from '@/components/pickerFields/DateRangePickerField.vue';

import { reactive, ref, toRaw } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import CustomerCodeAutocompleteField from '@/components/fields/CustomerCodeAutocompleteField.vue';
import KCheckboxField from '@/components/fields/KCheckboxField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import StateMultiSelectField from '@/components/fields/StateMultiSelectField.vue';
import DateRangePickerField from '@/components/pickerFields/DateRangePickerField.vue';
import useForm from '@/composables/useForm';
import { requiredValidationRule } from '@/helpers/validationRules';

export interface InitialData {
  dates?: DateRangePickerFieldValue | null;
  customer?: string | null;
  states?: string[] | null;
  search?: string | null;
  isOnly920Driver?: boolean;
  isExclude920Driver?: boolean;
  isFromWarehouse?: boolean;
  isToWarehouse?: boolean;
}

export interface ValidData {
  dates: DateRangePickerFieldValue;
  customer?: string | null;
  states?: string[] | null;
  search?: string | null;
  isOnly920Driver?: boolean;
  isExclude920Driver?: boolean;
  isFromWarehouse?: boolean;
  isToWarehouse?: boolean;
}

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({}),
});
const emit = defineEmits<{
  submit: [formData: ValidData];
}>();

const formData = reactive<InitialData>({});
const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, () => {
  emit('submit', toRaw(formData) as ValidData);
});

function handleIsOnly920DriverChange() {
  if (formData.isOnly920Driver) {
    formData.isExclude920Driver = false;
  }
}

function handleIsExclude920Driver() {
  if (formData.isExclude920Driver) {
    formData.isOnly920Driver = false;
  }
}
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.jobsAirRoadSearchForm"
    data-testid="jobsAirRoadSearchForm"
    @submit.prevent="handleSubmit"
  >
    <DateRangePickerField
      v-model="formData.dates"
      size="lg"
      label="Date range"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
      :class="$style.field"
    />
    <CustomerCodeAutocompleteField
      v-model="formData.customer"
      size="large"
      :disabled="props.loading"
      :class="$style.field"
      @keydown.enter="handleSubmit"
    />
    <StateMultiSelectField
      v-model="formData.states"
      size="large"
      :disabled="props.loading"
      :class="$style.field"
      @keydown.enter="handleSubmit"
    />
    <KTextField
      v-model="formData.search"
      size="lg"
      label="Search"
      :disabled="props.loading"
      :class="$style.field"
    />
    <div :class="$style.checkboxGroup">
      <KCheckboxField
        v-model="formData.isOnly920Driver"
        label="Only 920 driver"
        :class="$style.checkboxField"
        :disabled="props.loading"
        @change="handleIsOnly920DriverChange"
      />
      <KCheckboxField
        v-model="formData.isExclude920Driver"
        label="Exclude 920 driver"
        :class="$style.checkboxField"
        :disabled="props.loading"
        @change="handleIsExclude920Driver"
      />
      <KCheckboxField
        v-model="formData.isFromWarehouse"
        label="From warehouse"
        :class="$style.checkboxField"
        :disabled="props.loading"
      />
      <KCheckboxField
        v-model="formData.isToWarehouse"
        label="To warehouse"
        :class="$style.checkboxField"
        :disabled="props.loading"
      />
    </div>
    <KBtn
      type="submit"
      size="lg"
      prepend-icon="mdi-magnify"
      :loading="props.loading"
      :class="$style.submit"
    >
      Search
    </KBtn>
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.jobsAirRoadSearchForm {
}
  .field {
    width: 17.5rem;
    display: inline-block;
    vertical-align: top;
  }
  .field {
    margin-right: 1.5rem;
    margin-bottom: 0.5rem;
  }
  .checkboxGroup {
    margin-bottom: 0.5rem;
    display: flex;
    flex-wrap: wrap;
  }
    .checkboxField {
      margin-right: 1.5rem; // same as .v-selection-control--inline.v-radio
    }
  .submit {
    margin-bottom: calc(0.5rem + 22px);
  }
</style>
